import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import type { AssociatedFile } from "../components/associatedFiles/AssociatedFilesView";
import type { AdminCustomerResponse, CustomerFile } from "./customersApi";
import { addCustomerAttachment, deleteCustomerAttachment, deleteCustomerLogo, setCustomerLogo } from "./customersApi";

export const useCustomerFiles = (value: Pick<AdminCustomerResponse, "id" | "logo" | "files">) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<AssociatedFile[]>(customerResponseToFilesState(value));
  const [logo, setLogo] = useState<AssociatedFile | null>(customerResponseToLogoState(value));

  const addAttachmentMutation = useMutation<CustomerFile, unknown, FormData>({
    mutationFn: (formData) => addCustomerAttachment(value.id, formData),
    onSuccess: async (addedFile) => {
      const updatedFiles = [...files, { name: addedFile.name, url: addedFile.url }];
      setFiles(updatedFiles);
      enqueueSnackbar("Załącznik został dodany", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["customers", value.id] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać załącznika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const removeAttachmentMutation = useMutation<CustomerFile[], unknown, { idx: number }>({
    mutationFn: ({ idx }) => deleteCustomerAttachment(value.id, files[idx].name),
    onSuccess: async (updatedFiles) => {
      setFiles(updatedFiles);
      enqueueSnackbar("Załącznik został usunięty", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["customers", value.id] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć załącznika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const handleSetLogo = async (formData: FormData) => {
    const addedFile = await setCustomerLogo(value.id, formData);
    setLogo(addedFile);
  };

  const handleRemoveLogo = async () => {
    await deleteCustomerLogo(value.id);
    setLogo(null);
  };

  return {
    files,
    logo,
    handleAddAttachment: (v: FormData) => addAttachmentMutation.mutateAsync(v),
    handleRemoveAttachment: (idx: number) => removeAttachmentMutation.mutateAsync({ idx }),
    handleSetLogo,
    handleRemoveLogo,
  };
};

function customerResponseToFilesState(value: Pick<AdminCustomerResponse, "files">): AssociatedFile[] {
  return value.files.map((f) => ({ name: f.name, url: f.url }));
}

function customerResponseToLogoState(value: Pick<AdminCustomerResponse, "logo">): AssociatedFile | null {
  return value.logo ? { name: value.logo.name, url: value.logo.url } : null;
}
